import { joiResolver } from '@hookform/resolvers/joi'
import { LoadingButton } from '@mui/lab'
import { Dialog, Typography, DialogContent, DialogActions, Button, Box } from '@mui/material'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { FplDialogTitle, CardTitle, ControlledTextField } from 'components/common'
import {
  EmailSentStatus,
  SendAccountPayableInvoiceToEmailMutation,
  useSendAccountPayableInvoiceToEmailMutation,
} from 'generated/graphql'

import ValidationSchema from './ValidationSchema'

interface ISendToDialogProps {
  isOpen: boolean
  onClose: () => void
  accountPayableRecordId: number
}

type SendToForm = {
  email: string
}

function SendToDialog({ isOpen, onClose, accountPayableRecordId }: ISendToDialogProps) {
  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<SendToForm>({
    resolver: joiResolver(ValidationSchema),
    defaultValues: {
      email: '',
    },
    mode: 'onChange',
  })

  const handleOnClose = () => {
    onClose()
    reset()
  }

  const onCompleted = (data: SendAccountPayableInvoiceToEmailMutation) => {
    if (data.sendAccountPayableInvoiceToEmail === EmailSentStatus.Success) {
      toast.success('Email was submitted with success')
      handleOnClose()
    } else {
      toast.warning('Something went wrong. Please try again later.')
    }
  }

  // Mutations
  const [sendAccountPayableInvoiceToEmail, { loading: invoiceSendToLoading }] =
    useSendAccountPayableInvoiceToEmailMutation({
      onCompleted: onCompleted,
    })

  const onSubmit = (data: SendToForm) => {
    sendAccountPayableInvoiceToEmail({
      variables: {
        input: {
          sendTo: data.email,
          accountPayableRecordId: accountPayableRecordId,
        },
      },
    })
  }

  return (
    <Dialog fullWidth open={isOpen}>
      <FplDialogTitle onClose={handleOnClose}>
        <CardTitle>Send To</CardTitle>
        <Typography variant='body1'>Please enter an e-mail address to send.</Typography>
      </FplDialogTitle>

      <DialogContent>
        <Box paddingTop={1}>
          <ControlledTextField
            control={control}
            label='Email'
            name='email'
            required
            type='email'
            error={!!errors.email}
            helperText={errors.email?.message}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          disabled={invoiceSendToLoading}
          variant='contained'
          color='grey'
          onClick={handleOnClose}>
          Cancel
        </Button>

        <LoadingButton
          disabled={invoiceSendToLoading}
          loading={invoiceSendToLoading}
          variant='contained'
          onClick={handleSubmit(onSubmit)}>
          Send
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
export default SendToDialog
